import { i18n } from '../i18n/i18n.js'

import { 
    countProductsInBuilding,
    countProductsOnTier } from '../utils/productUtils.js'
import { getTierName } from '../utils/productFormatters.js';
import { HERO_OUTLINE } from '../constants/heroOutline';
import { OUTLINE_PREVIEW_MODE } from '../constants/outlinePreviewMode';

import { OutlinePreview } from '../../domains/hero/components/OutlinePreview/OutlinePreview';
import { OutlineStatusPreview } from '../ui/OutlineStatusPreview/OutlineStatusPreview';
import { futurePhaseInquiry } from '../utils/queryEvents.js';
import { ProductPreview } from '../ui/ProductPreview/ProductPreview.jsx';
import { ProductPreviewMobile } from '../ui/ProductPreviewMobile/ProductPreviewMobile.jsx';


export const PREVIEW_MODE_STRATEGY = {
    // Product - specific mode cases ---------------------------------------------
    [OUTLINE_PREVIEW_MODE.NORMAL]: { //TODO: define here, now handled directly in Components
        component: ProductPreview,
        mobileComponent: ProductPreviewMobile,
        isInteractive: true,
        getProps: (data, context) => {
            const { product, mode} = data;
            return {
                product,
                onAction: () => PREVIEW_MODE_STRATEGY[OUTLINE_PREVIEW_MODE.NORMAL].executeAction(data, context)
            }
        },
        executeAction: (data, context) => {
            const { product} = data;
            const uuid = product?.uuid

            if (uuid) {
                context.actions.selectProductHandler(
                    uuid, 
                    { 
                        buildingId: product.building_id, 
                        refTier: context.activeTier ?? product.ref_tier
                    })
            }
        },
    },

    [OUTLINE_PREVIEW_MODE.HIDDEN]: {
        component: OutlineStatusPreview,
        mobileComponent: OutlineStatusPreview,
		isInteractive: false,
        getProps: (data, context) => {
            const { product} = data;
            const { 
                defaultProductCategory,
                getProductsCategoryString
            } = context;

            if(!product) return {}

            return {
                title: product.id_original,
                message: getProductsCategoryString('inaccessible', product.product_category ?? defaultProductCategory),
                showCta: false,
                ctaText: null, // no action
            }
        },
        executeAction: () => {},
    },

    [OUTLINE_PREVIEW_MODE.FILTERED]: {
        component: OutlineStatusPreview,
        mobileComponent: OutlineStatusPreview,
		isInteractive: false,
        getProps: (data, context) => {
            const { product} = data;
            const { 
                defaultProductCategory,
                getProductsCategoryString
            } = context;

            if(!product) return {}

            return {
                title: product.id_original,
                message: getProductsCategoryString('hidden_by_filter', product.product_category ?? defaultProductCategory),
                showCta: false,
                ctaText: null, // no action
            }
        },
        executeAction: () => {},
    },

    [OUTLINE_PREVIEW_MODE.FILTERED_SOLD]: {
        component: OutlineStatusPreview,
        mobileComponent: OutlineStatusPreview,
		isInteractive: false,
        getProps: (data, context) => {
            const { product} = data;
            const { 
                defaultProductCategory,
                getProductsCategoryString
            } = context;

            if(!product) return {}

            return {
                title: product.id_original,
                message: getProductsCategoryString('sold', product.product_category ?? defaultProductCategory),
                showCta: false,
                ctaText: null, // no action
            }
        },
        executeAction: () => {},
    },

    [OUTLINE_PREVIEW_MODE.SOLD]: {
        component: OutlineStatusPreview,
        mobileComponent: OutlineStatusPreview,
		isInteractive: false,
        getProps: (data, context) => {
            const { product} = data;
            const { 
                defaultProductCategory,
                getProductsCategoryString
            } = context;

            if(!product) return {}

            return {
                title: product.id_original,
                message: getProductsCategoryString('sold', product.product_category ?? defaultProductCategory),
                showCta: false,
                ctaText: null, // no action
            }
        },
        executeAction: () => {},
    },
    
    // Building, tier and product mode cases -------------------------------------
	[OUTLINE_PREVIEW_MODE.NORMAL_OUTLINE]: {
		component: OutlinePreview,
		mobileComponent: OutlinePreview,
		isInteractive: true,
        getProps: (data, context) => {
            const { building, tier, type } = data;
            const { 
                actions, 
                activeFiltersCount, 
                allProducts,
                defaultProductCategory,
                filteredProducts,
                getProductsCategoryString
            } = context;

			const isBuilding = type === HERO_OUTLINE.BUILDINGS;
            const buildingLabel = isBuilding ? i18n.t('common.building') : null;
            const buildingName = isBuilding ? building?.name_original : null;
            const label = isBuilding ? i18n.t('common.building') : i18n.t('common.floor');
            const name = isBuilding
                ? building.name_original ?? building?.id_cad ?? null
                : getTierName(tier, true)

            const hasFilters = activeFiltersCount > 0;
            const filtered = isBuilding
                ? countProductsInBuilding(filteredProducts, building.id_cad)
                : countProductsOnTier(filteredProducts, tier.ref_tier, building?.id_cad ?? null);
            const total = isBuilding
                ? countProductsInBuilding(allProducts, building.id_cad)
                : countProductsOnTier(allProducts, tier.ref_tier, building?.id_cad ?? null);

            const countLabel = hasFilters && filtered != null
                ? i18n.t('hero.available_filtered', { count: filtered })
                : getProductsCategoryString('available', defaultProductCategory, { count: total })
            const countValue = hasFilters && filtered != null
                ? `${filtered} / ${total}`
                : total

            const ctaText = isBuilding 
                ? i18n.t('hero.see_building') 
                : i18n.t('hero.see_floor');

			return {
                buildingLabel,
                buildingName,
                countLabel,
                countValue,
                ctaText,
                hasFilters,
                isBuilding,
                label,
                name,
                showCta: true,

				onAction: () => {
					if (isBuilding) {
						actions.goToBuildingModel(building?.id_cad);
					} else {
						actions.goToTierSection(
							tier?.ref_tier,
							building?.id_cad,
						);
					}
				},
			};
        },
        executeAction: (data, context) => {
            const isBuilding = data.type === HERO_OUTLINE.BUILDINGS;

            if (isBuilding) {
                context.actions.goToBuildingModel(data.building?.id_cad);
            } else {
                context.actions.goToTierSection(
                    data.tier?.ref_tier,
                    data.building?.id_cad,
                );
            }
        },
	},

    [OUTLINE_PREVIEW_MODE.OTHER_STAGE]: {
        component: OutlineStatusPreview,
        mobileComponent: OutlineStatusPreview,
        isInteractive: false,
        getProps: (data, context) => {
            const { building, product, type } = data;
            const {
                defaultProductCategory,
                getProductsCategoryString
            } = context;

            const isProduct = type === HERO_OUTLINE.APARTMENTS;

            const isPhaseDescription = building?.phase_description?.length > 0
            const message = isPhaseDescription
                ? building.phase_description
                : isProduct
                    ? getProductsCategoryString('other_stage', product.product_category, {count: 1 })
                    : getProductsCategoryString('other_stage', defaultProductCategory, { count: 2 })
            
            return {
                title: building.phase_name,
                message,
                showCta: false,
                ctaText: null, // no action
            }
        },
        executeAction: () => {},
    },

	[OUTLINE_PREVIEW_MODE.OTHER_STAGE_INQUIRY]: {
        component: OutlineStatusPreview,
        mobileComponent: OutlineStatusPreview,
        isInteractive: true,
        getProps: (data, context) => {
            const { building, product, type } = data;
            const {
                defaultProductCategory,
                getProductsCategoryString
            } = context;

            const isProduct = type === HERO_OUTLINE.APARTMENTS;

            const isPhaseDescription = building?.phase_description?.length > 0
            const message = isPhaseDescription
                ? building.phase_description
                : isProduct
                    ? getProductsCategoryString('other_stage', product.product_category, {count: 1 })
                    : getProductsCategoryString('other_stage', defaultProductCategory, { count: 2 })
            
            return {
                title: building.phase_name,
                message,
                showCta: true,
                ctaText: i18n.t('common.send_inquiry'),

                onAction: () => {}
            }
        },
        executeAction: (data, context) => {
            const { building } = data;
            const phase = {
                phaseName: building.phase_name,
                buildingName: building.name_original
            }

            futurePhaseInquiry(phase)
        },
    },

	[OUTLINE_PREVIEW_MODE.OTHER_STAGE_REDIRECT]: {
        component: OutlineStatusPreview,
        mobileComponent: OutlineStatusPreview,
        isInteractive: true,
        getProps: (data, context) => {
            const { building, product, type } = data;
            const {
                defaultProductCategory,
                getProductsCategoryString
            } = context;

            const isProduct = type === HERO_OUTLINE.APARTMENTS;

            const isPhaseDescription = building?.phase_description?.length > 0
            const message = isPhaseDescription
                ? building.phase_description
                : isProduct
                    ? getProductsCategoryString('other_stage', product.product_category, {count: 1 })
                    : getProductsCategoryString('other_stage', defaultProductCategory, { count: 2 })
            
            return {
                title: building.phase_name,
                message,
                showCta: true,
                ctaText: i18n.t('common.open_phase_page'),

                onAction: () => PREVIEW_MODE_STRATEGY[OUTLINE_PREVIEW_MODE.OTHER_STAGE_REDIRECT].executeAction(data, context)
            }
        },
        executeAction: (data, context) => {
            const url = data.building?.redirect_to_app_url
            if (url) window.open(url, '_blank')
        },
    },

	[OUTLINE_PREVIEW_MODE.DISABLED_INACTIVE]: {
        component: OutlineStatusPreview,
        mobileComponent: OutlineStatusPreview,
        isInteractive: false,
        getProps: (data, context) => {
            const { building, tier, type } = data;
            const {
                defaultProductCategory,
                getProductsCategoryString
            } = context;
            
			const isBuilding = type === HERO_OUTLINE.BUILDINGS;

            const title = isBuilding 
                ? i18n.t('common.building_name', { name: building.name_original })
                : i18n.t('common.floor_name', { name: getTierName(tier, true) })

            const inactiveString = isBuilding ? 'inactive_building' : 'inactive_tier';
            const message = getProductsCategoryString(inactiveString, defaultProductCategory)

            return {
                title,
                message,
                showCta: false,
                ctaText: null, // no action
            }
        },
        executeAction: () => {},
    },

	[OUTLINE_PREVIEW_MODE.DISABLED_SOLD_OUT]: {
        component: OutlineStatusPreview,
        mobileComponent: OutlineStatusPreview,
        isInteractive: false,
        getProps: (data, context) => {
            const { building, tier, type } = data;
            const {
                defaultProductCategory,
                getProductsCategoryString
            } = context;
            
			const isBuilding = type === HERO_OUTLINE.BUILDINGS;

            const title = isBuilding 
                ? i18n.t('common.building_name', { name: building.name_original })
                : i18n.t('common.floor_name', { name: getTierName(tier, true) })

            const soldOutString = isBuilding ? 'sold_out_building' : 'sold_out_tier';
            const message = getProductsCategoryString(soldOutString, defaultProductCategory)

            return {
                title,
                message,
                showCta: false,
                ctaText: null, // no action
            }
        },
        executeAction: () => {},
    },
};
